import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { STEP_COUNT } from './steps';

// A shared browser holds several accounts over time, and the guide is offered
// once per account, not once per device. Capped so a kiosk cannot grow the entry
// without bound; the oldest completion is the one worth forgetting.
const MAX_REMEMBERED_USERS = 20;

type State = {
  completedUserIds: string[];
  stepIndex: number;
  experienceId: string | null;
  /** Who the tour in progress belongs to, so another account cannot resume it. */
  userId: string | null;
};

type Actions = {
  start: (experienceId: string, userId: string) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  finish: () => void;
  reset: () => void;
};

const INITIAL: State = {
  completedUserIds: [],
  stepIndex: 0,
  experienceId: null,
  userId: null,
};

// What comes back from storage was written by whichever build stored it, and
// the card list has already been shortened once. Anything that is not an index
// this build has reads as the first card.
const clampStepIndex = (value: unknown) => {
  const index = typeof value === 'number' && Number.isFinite(value) ? value : 0;

  return Math.min(Math.max(index, 0), STEP_COUNT - 1);
};

const complete = (state: State): Partial<State> => ({
  completedUserIds: state.userId
    ? [
        ...state.completedUserIds.filter((id) => id !== state.userId),
        state.userId,
      ].slice(-MAX_REMEMBERED_USERS)
    : state.completedUserIds,
  experienceId: null,
  userId: null,
});

const usePlannerOnboardingStore = createWithEqualityFn<State & Actions>()(
  persist(
    (set, get) => ({
      ...INITIAL,
      start: (experienceId, userId) => {
        if (get().completedUserIds.includes(userId)) return;
        set({ experienceId, userId, stepIndex: 0 });
      },
      next: () =>
        set((state) => ({
          stepIndex: Math.min(state.stepIndex + 1, STEP_COUNT - 1),
        })),
      back: () =>
        set((state) => ({ stepIndex: Math.max(state.stepIndex - 1, 0) })),
      skip: () => set(complete),
      finish: () => set(complete),
      reset: () => set(INITIAL),
    }),
    {
      name: '_ah_planner_onboarding',
      partialize: ({ completedUserIds, stepIndex, experienceId, userId }) => ({
        completedUserIds,
        stepIndex,
        experienceId,
        userId,
      }),
      // Normalised on the way in, so everything counting from it afterwards
      // counts from a card that exists. Clamping only at render left back()
      // decrementing a number the screen was never showing, and the first
      // presses went nowhere.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<State>) };

        return { ...merged, stepIndex: clampStepIndex(merged.stepIndex) };
      },
    }
  ),
  shallow
);

export const usePlannerOnboarding = (
  experienceId: string | undefined,
  userId: string | undefined
) => {
  const {
    completedUserIds,
    stepIndex,
    experienceId: tourExperienceId,
    userId: tourUserId,
  } = usePlannerOnboardingStore();

  // Hydration already normalised this, so it is the render's own guarantee
  // rather than the only one: an index past the end reads as no card at all,
  // leaving the run active with nothing on screen and no way to finish it.
  const safeIndex = clampStepIndex(stepIndex);

  return {
    isActive:
      Boolean(experienceId) &&
      Boolean(userId) &&
      tourExperienceId === experienceId &&
      tourUserId === userId &&
      !completedUserIds.includes(userId as string),
    stepIndex: safeIndex,
    isFirstStep: safeIndex === 0,
    isLastStep: safeIndex === STEP_COUNT - 1,
  };
};

export const usePlannerOnboardingActions = () => {
  const { start, next, back, skip, finish } = usePlannerOnboardingStore();

  return { start, next, back, skip, finish };
};

/**
 * Non-reactive facade for callers outside React: the planner-creation call
 * sites, which decide which redirect to build before any onboarding component
 * has mounted, and the Help panel's restart action.
 */
export const shouldOfferPlannerOnboarding = (userId: string | undefined) =>
  Boolean(userId) &&
  !usePlannerOnboardingStore.getState().completedUserIds.includes(userId!);

export const startPlannerOnboarding = (experienceId: string, userId: string) =>
  usePlannerOnboardingStore.getState().start(experienceId, userId);

export const getSavedPlannerOnboardingUserId = (experienceId: string) => {
  const { experienceId: savedExperienceId, userId } =
    usePlannerOnboardingStore.getState();

  return savedExperienceId === experienceId ? userId : null;
};

export const skipPlannerOnboarding = () =>
  usePlannerOnboardingStore.getState().skip();

/**
 * Forgets one account's completion, so Help's restart hands the guide back to
 * whoever asked for it without also re-offering it to everyone else who has
 * used this browser. Given no id, forgets all of them (tests).
 */
export const resetPlannerOnboarding = (userId?: string) => {
  const store = usePlannerOnboardingStore.getState();

  if (!userId) {
    store.reset();
    return;
  }

  // The run in progress belongs to whoever started it. Another account signed
  // in on this browser can be mid-tour, and forgetting one account's completion
  // is no reason to throw away someone else's place in theirs.
  const isOwnRun = store.userId === userId;

  usePlannerOnboardingStore.setState({
    completedUserIds: store.completedUserIds.filter((id) => id !== userId),
    ...(isOwnRun && { experienceId: null, userId: null, stepIndex: 0 }),
  });
};
